import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUsers, FiPhone, FiMail, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RestaurantCaptains = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [captains, setCaptains] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRestaurantDetails();
    fetchCaptains();
  }, [restaurantId]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}`);
      if (response.data.success) setRestaurant(response.data.data);
    } catch {
      toast.error('Failed to fetch restaurant details');
    }
  };

  const fetchCaptains = async () => {
    try {
      const response = await api.get(`/vendor-owner/restaurant/${restaurantId}/captains`);
      if (response.data.success) {
        setCaptains(response.data.data);
      }
    } catch {
      toast.error('Failed to fetch captains');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/vendor-owner/restaurants')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
          >
            <FiArrowLeft /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {restaurant?.restaurantDetails?.restaurantName}
            </h1>
            <p className="text-gray-500 text-sm">Captains Management</p>
          </div>
        </div>

        <button
          onClick={fetchCaptains}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {[
          { label: 'Total', value: captains.length },
          { label: 'Active', value: captains.filter(c => c.isActive).length },
          { label: 'Inactive', value: captains.filter(c => !c.isActive).length }
        ].map((item, i) => (
          <div key={i} className="bg-white p-5 rounded-xl shadow-sm">
            <p className="text-sm text-gray-500">{item.label}</p>
            <h2 className="text-2xl font-bold text-gray-900">{item.value}</h2>
          </div>
        ))}
      </div>

      {/* List */}
      <div className="mt-6 grid gap-4">
        {captains.map((captain) => (
          <div
            key={captain._id}
            className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold">
                {captain.name[0]}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{captain.name}</h3>
                <p className="text-sm text-gray-500">@{captain.username}</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-1"><FiMail /> {captain.email}</span>
              <span className="flex items-center gap-1"><FiPhone /> {captain.contactNumber}</span>
              <span className="flex items-center gap-1"><FiCalendar /> {new Date(captain.createdAt).toLocaleDateString()}</span>
            </div>

            <span className={`px-3 py-1 text-xs rounded-full font-medium ${captain.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
              {captain.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RestaurantCaptains;